import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toPng } from 'html-to-image';
import { X, Download, Send, Trophy, MessageCircle, Loader2, Check } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useUnits } from '../context/UnitsContext';
import { useMyChallenges } from '../lib/queries';
import { createPost } from '../lib/api';
import WeightShareCard from './WeightShareCard';

/**
 * Bottom-sheet modal that turns the user's weight chart (in the
 * currently-selected period) into a shareable PNG and exposes three
 * actions: post to public feed, post to one or more challenges, share
 * the raw image via the system share sheet (WhatsApp, etc.).
 *
 * The off-screen <WeightShareCard> is rendered into the DOM (visually
 * hidden) and captured with html-to-image when the user picks an
 * action — this guarantees the same pixel-perfect look across devices.
 */
export default function ShareWeightModal({
  isOpen,
  onClose,
  isDark,
  user,
  plan,
  chartData,
  chartPeriod,
  weightLost,
  streakDays,
  formatNumber,
  convertWeight,
  weightUnit,
}) {
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(null); // 'feed' | 'whatsapp' | 'challenges' | null
  const [showChallengePicker, setShowChallengePicker] = useState(false);
  const [selectedChallengeIds, setSelectedChallengeIds] = useState([]);
  const [toast, setToast] = useState(null);

  const { data: myChallenges = [] } = useMyChallenges();
  // Show only challenges the user actually participates in (active ones).
  const eligibleChallenges = (myChallenges || []).filter(
    (c) => c.status !== 'ended' && (c.participants || []).some((p) => p.user_id === user?.user_id)
  );

  // Reset state every time the modal closes
  useEffect(() => {
    if (!isOpen) {
      setBusy(null);
      setShowChallengePicker(false);
      setSelectedChallengeIds([]);
      setToast(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Compute days since plan start for the "Giorni" stat
  let daysCount = 0;
  try {
    const start = new Date(plan?.start_date);
    daysCount = Math.max(0, Math.floor((Date.now() - start.getTime()) / 86400000));
  } catch (e) {}

  const userName = user?.name?.split(' ')[0] || 'Utente';

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  // Capture the off-screen card to a PNG dataURL (returns null on failure)
  const captureImage = async () => {
    if (!cardRef.current) return null;
    try {
      // 2x pixel ratio = retina-quality share image
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#FFFFFF',
      });
      return dataUrl;
    } catch (err) {
      console.error('Image capture failed:', err);
      return null;
    }
  };

  const dataUrlToBlob = async (dataUrl) => {
    const res = await fetch(dataUrl);
    return await res.blob();
  };

  const handleShareToFeed = async () => {
    setBusy('feed');
    try {
      const dataUrl = await captureImage();
      if (!dataUrl) throw new Error('capture_failed');
      // Strip the data:image/png;base64, prefix — backend expects raw b64
      const base64 = dataUrl.split(',')[1];
      await createPost({
        post_type: 'progress',
        text: t('weightShare.captionFeed', 'Il mio andamento peso su Slimmy'),
        image_base64: base64,
      });
      showToast(t('weightShare.publishedFeed', 'Pubblicato nel feed!'));
      setTimeout(onClose, 1200);
    } catch (err) {
      console.error(err);
      showToast(t('weightShare.error', 'Errore durante la condivisione'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleShareToChallenges = async () => {
    if (selectedChallengeIds.length === 0) return;
    setBusy('challenges');
    try {
      const dataUrl = await captureImage();
      if (!dataUrl) throw new Error('capture_failed');
      const base64 = dataUrl.split(',')[1];
      await createPost({
        post_type: 'progress',
        text: t('weightShare.captionChallenge', 'Il mio andamento peso su Slimmy'),
        image_base64: base64,
        challenge_ids: selectedChallengeIds,
      });
      showToast(t('weightShare.publishedChallenges', 'Pubblicato nelle sfide!'));
      setTimeout(onClose, 1200);
    } catch (err) {
      console.error(err);
      showToast(t('weightShare.error', 'Errore durante la condivisione'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleShareWhatsapp = async () => {
    setBusy('whatsapp');
    try {
      const dataUrl = await captureImage();
      if (!dataUrl) throw new Error('capture_failed');
      const blob = await dataUrlToBlob(dataUrl);
      const file = new File([blob], `slimmy-progress-${Date.now()}.png`, { type: 'image/png' });
      const shareText = t('weightShare.captionFeed', 'Il mio andamento peso su Slimmy');

      // Prefer the Web Share API (mobile + Capacitor): the user picks
      // WhatsApp from the system share sheet and the image is attached.
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          text: shareText,
          title: 'Slimmy',
        });
        showToast(t('weightShare.shared', 'Condivisione avviata'));
        setTimeout(onClose, 1000);
        return;
      }

      // Fallback (desktop browsers without Web Share files): download
      // the image and open WhatsApp Web with the caption pre-filled —
      // the user attaches the just-downloaded file manually.
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = file.name;
      link.click();
      const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
      window.open(waUrl, '_blank');
      showToast(t('weightShare.downloadedHint', 'Immagine scaricata, allegala su WhatsApp'));
    } catch (err) {
      // User-cancelled share is not a real error — silence it
      if (err?.name !== 'AbortError') {
        console.error(err);
        showToast(t('weightShare.error', 'Errore durante la condivisione'), 'error');
      }
    } finally {
      setBusy(null);
    }
  };

  const toggleChallenge = (cid) => {
    setSelectedChallengeIds((prev) =>
      prev.includes(cid) ? prev.filter((x) => x !== cid) : [...prev, cid]
    );
  };

  return createPortal(
    <>
      {/* Off-screen card we'll capture — kept in the DOM but invisible */}
      <div
        style={{
          position: 'fixed',
          left: -10000,
          top: 0,
          pointerEvents: 'none',
          opacity: 0,
        }}
        aria-hidden
      >
        <WeightShareCard
          ref={cardRef}
          name={userName}
          chartData={chartData}
          chartTitle={t('weightEntry.trend', 'Andamento Peso')}
          targetWeight={plan?.target_weight || 0}
          weightLost={weightLost}
          daysCount={daysCount}
          streakDays={streakDays}
          weightUnit={weightUnit}
          formatNumber={formatNumber}
          convertWeight={convertWeight}
          labels={{
            lost: t('dashboard.lost', 'Persi'),
            days: t('weightShare.daysLabel', 'Giorni'),
            streak: t('dashboard.streak', 'Serie'),
            goal: t('dashboard.goal', 'Obiettivo'),
            actual: t('dashboard.weightCard.actualCurve', 'Peso reale'),
            expected: t('dashboard.weightCard.expectedCurve', 'Curva prevista'),
          }}
        />
      </div>

      {/* Bottom sheet */}
      <div
        className="fixed inset-0 flex items-end justify-center bg-black/60 backdrop-blur-sm"
        style={{ zIndex: 9999 }}
        onClick={() => !busy && onClose()}
        data-testid="share-weight-modal"
      >
        <div
          className={`w-full max-w-md rounded-t-3xl p-6 animate-slide-up ${
            isDark ? 'bg-[#0D1424]' : 'bg-white'
          }`}
          style={{ paddingBottom: 'calc(28px + env(safe-area-inset-bottom, 0px))' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('weightShare.title', 'Condividi i tuoi progressi')}
              </h2>
              <p className={`text-xs mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('weightShare.subtitle', 'Periodo selezionato')}: <span className="font-semibold">
                  {chartPeriod === 'all' ? t('common.all', 'Tutti') : chartPeriod.toUpperCase()}
                </span>
              </p>
            </div>
            <button
              onClick={() => !busy && onClose()}
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDark ? 'bg-white/10' : 'bg-gray-100'
              }`}
              data-testid="share-weight-close-btn"
            >
              <X className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
            </button>
          </div>

          {!showChallengePicker ? (
            <div className="space-y-3">
              <ShareAction
                icon={<Send className="w-5 h-5" />}
                color="#16A34A"
                label={t('weightShare.publishToFeed', 'Pubblica nel feed')}
                desc={t('weightShare.publishToFeedDesc', 'Visibile a tutta la community')}
                isDark={isDark}
                loading={busy === 'feed'}
                disabled={!!busy}
                onClick={handleShareToFeed}
                testid="share-weight-feed-btn"
              />
              <ShareAction
                icon={<Trophy className="w-5 h-5" />}
                color="#F59E0B"
                label={t('weightShare.publishToChallenges', 'Pubblica in una sfida')}
                desc={
                  eligibleChallenges.length > 0
                    ? t('weightShare.publishToChallengesDesc', 'Seleziona una o più sfide')
                    : t('weightShare.noChallenges', 'Nessuna sfida attiva')
                }
                isDark={isDark}
                disabled={!!busy || eligibleChallenges.length === 0}
                onClick={() => setShowChallengePicker(true)}
                testid="share-weight-challenges-btn"
              />
              <ShareAction
                icon={<MessageCircle className="w-5 h-5" />}
                color="#25D366"
                label={t('weightShare.shareWhatsapp', 'Invia via WhatsApp')}
                desc={t('weightShare.shareWhatsappDesc', 'O qualsiasi altra app')}
                isDark={isDark}
                loading={busy === 'whatsapp'}
                disabled={!!busy}
                onClick={handleShareWhatsapp}
                testid="share-weight-whatsapp-btn"
              />
            </div>
          ) : (
            <div>
              <p className={`text-sm mb-3 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                {t('weightShare.selectChallenges', 'Seleziona una o più sfide:')}
              </p>
              <div className="max-h-[40vh] overflow-y-auto -mx-1 px-1 mb-4 space-y-2">
                {eligibleChallenges.map((c) => {
                  const checked = selectedChallengeIds.includes(c.challenge_id);
                  return (
                    <button
                      key={c.challenge_id}
                      onClick={() => toggleChallenge(c.challenge_id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
                        checked
                          ? isDark
                            ? 'bg-[#E8334A]/20 border-2 border-[#E8334A]'
                            : 'bg-[#E8334A]/10 border-2 border-[#E8334A]'
                          : isDark
                          ? 'bg-white/5 border-2 border-transparent'
                          : 'bg-gray-100 border-2 border-transparent'
                      }`}
                      data-testid={`share-challenge-option-${c.challenge_id}`}
                    >
                      <div
                        className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${
                          checked ? 'bg-[#E8334A]' : isDark ? 'bg-white/10' : 'bg-white'
                        }`}
                        style={{ border: checked ? 'none' : '1.5px solid #94a3b8' }}
                      >
                        {checked && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {c.emoji ? `${c.emoji} ` : ''}{c.name}
                        </div>
                        <div className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                          {(c.participants || []).length} {t('challenges.participants', 'partecipanti')}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowChallengePicker(false)}
                  disabled={!!busy}
                  className={`flex-1 py-3 rounded-xl text-sm font-semibold ${
                    isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'
                  }`}
                  data-testid="share-weight-back-btn"
                >
                  {t('common.back', 'Indietro')}
                </button>
                <button
                  onClick={handleShareToChallenges}
                  disabled={busy === 'challenges' || selectedChallengeIds.length === 0}
                  className={`flex-1 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 ${
                    selectedChallengeIds.length > 0 && busy !== 'challenges'
                      ? 'bg-[#E8334A] text-white'
                      : 'bg-[#E8334A]/40 text-white/70 cursor-not-allowed'
                  }`}
                  data-testid="share-weight-publish-challenges-btn"
                >
                  {busy === 'challenges' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {t('weightShare.publishCount', 'Pubblica')}
                      {selectedChallengeIds.length > 0 ? ` (${selectedChallengeIds.length})` : ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Toast */}
          {toast && (
            <div
              className={`mt-4 rounded-xl px-4 py-3 text-sm text-center ${
                toast.type === 'error'
                  ? 'bg-red-500/15 text-red-400'
                  : 'bg-green-500/15 text-green-500'
              }`}
            >
              {toast.message}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

function ShareAction({ icon, color, label, desc, isDark, onClick, disabled, loading, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-full flex items-center gap-4 p-4 rounded-2xl text-left transition-opacity ${
        isDark ? 'bg-white/5' : 'bg-gray-50'
      } ${disabled ? 'opacity-50' : 'active:scale-[0.98]'}`}
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-white"
        style={{ background: color }}
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {label}
        </div>
        <div className={`text-xs mt-0.5 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
          {desc}
        </div>
      </div>
    </button>
  );
}
